import { DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import {
  AccountctlRunSummary,
  AccountctlScenarioSummary,
  useComposeAccountctlRunMutation,
  useCreateAccountctlRunMutation,
  useImportAccountctlRunMutation,
  useListAccountctlRunsQuery,
  useListAccountctlScenariosQuery,
} from '@/services/accountctlApi';
import { PalmettoButton, PalmettoModal, PalmettoTag, PalmettoTextarea } from '@palmetto/dsm-components';
import { Column, Table, toast } from '@palmetto/palmetto-components';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { AccountctlRunStatus } from 'types';

const RUN_HISTORY_LIMIT = 20;

interface StatusPillProps {
  status: AccountctlRunStatus;
}

const statusTagColor = {
  running: 'volt',
  ok: 'success',
  error: 'error',
  cancelled: 'warning',
} as const satisfies Record<AccountctlRunStatus, 'volt' | 'success' | 'error' | 'warning'>;

const StatusPill = ({ status }: StatusPillProps) => (
  <PalmettoTag color={statusTagColor[status]} label={status} size="sm" />
);

const formatRelative = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const deltaMs = Date.now() - then;
  const seconds = Math.floor(deltaMs / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

interface RunModalProps {
  scenario: AccountctlScenarioSummary | null;
  onClose: () => void;
}

interface PreconditionRow {
  label: string;
  value: string;
}

const summarizePreconditions = (preconditions: AccountctlScenarioSummary['preconditions']): PreconditionRow[] => {
  const rows: PreconditionRow[] = [];
  if (!preconditions) return rows;

  const orgs = (preconditions as { organization?: Array<{ programType?: string; state?: string; lseId?: number }> })
    .organization;
  for (const org of orgs ?? []) {
    const parts: string[] = [];
    if (org.programType) parts.push(org.programType);
    if (org.state) parts.push(org.state);
    if (typeof org.lseId === 'number') parts.push(`lseId ${org.lseId}`);
    rows.push({ label: 'Organization', value: parts.join(' · ') || '—' });
  }

  const ldFlags = (preconditions as { ldFlags?: Record<string, unknown> }).ldFlags;
  if (ldFlags) {
    for (const [flag, value] of Object.entries(ldFlags)) {
      rows.push({ label: 'LD flag', value: `${flag} = ${JSON.stringify(value)}` });
    }
  }

  const roles = (preconditions as { roles?: string[] }).roles;
  if (roles?.length) {
    rows.push({ label: 'Roles', value: roles.join(', ') });
  }

  return rows;
};

const RunModal = ({ scenario, onClose }: RunModalProps) => {
  const navigate = useNavigate();
  const [createRun, { isLoading }] = useCreateAccountctlRunMutation();

  const preconditionRows = useMemo(() => summarizePreconditions(scenario?.preconditions), [scenario?.preconditions]);

  const handleSubmit = useCallback(async () => {
    if (!scenario) return;
    try {
      const result = await createRun({ scenarioId: scenario.id }).unwrap();
      onClose();
      navigate(`/internal/accountctl/runs/${result.runId}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start run';
      toast.error(message);
    }
  }, [createRun, navigate, onClose, scenario]);

  return (
    <PalmettoModal
      open={!!scenario}
      onClose={onClose}
      invertedBackground={false}
      bottomContent={
        <div className="flex justify-end gap-8">
          <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={onClose} disabled={isLoading}>
            Cancel
          </PalmettoButton>
          <PalmettoButton type="button" emphasis="primary" layout="hug" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? 'Starting…' : 'Run scenario'}
          </PalmettoButton>
        </div>
      }
    >
      <div className="flex flex-col gap-16">
        <div className="flex flex-col gap-4">
          <h2 className="heading-300">Run {scenario?.id}</h2>
          {scenario?.description && <p className="text-text-secondary">{scenario.description}</p>}
        </div>
        {preconditionRows.length > 0 && (
          <div className="flex flex-col gap-8">
            <div className="text-sm font-bold">Preconditions</div>
            <ul className="flex flex-col gap-4 text-sm">
              {preconditionRows.map((row, i) => (
                <li key={`${row.label}-${i}`} className="flex gap-8">
                  <span className="min-w-[120px] text-text-secondary">{row.label}</span>
                  <span className="font-mono">{row.value}</span>
                </li>
              ))}
            </ul>
            <div className="text-xs text-text-secondary">
              The harness verifies these at preflight. Fix anything missing before running.
            </div>
          </div>
        )}
      </div>
    </PalmettoModal>
  );
};

const ComposeBox = () => {
  const navigate = useNavigate();
  const [description, setDescription] = useState('');
  const [compose, { isLoading }] = useComposeAccountctlRunMutation();

  const handleSubmit = useCallback(async () => {
    if (!description.trim()) return;
    try {
      const result = await compose({ description: description.trim() }).unwrap();
      navigate(`/internal/accountctl/runs/${result.runId}`);
    } catch (err) {
      const apiMessage =
        err && typeof err === 'object' && 'data' in err
          ? (err as { data?: { message?: string } }).data?.message
          : undefined;
      toast.error(apiMessage ?? (err instanceof Error ? err.message : 'Failed to create account'));
    }
  }, [compose, description, navigate]);

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value),
    [],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        void handleSubmit();
      }
    },
    [handleSubmit],
  );

  return (
    <div className="flex flex-col gap-8">
      <PalmettoTextarea
        id="account-description"
        label="Describe the account you need"
        value={description}
        onChange={handleDescriptionChange}
        onKeyDown={handleKeyDown}
        placeholder='e.g. "an HVAC account in Texas that is post-install" or "a declined solar credit application"'
        rows={3}
        disabled={isLoading}
        className="resize-none"
      />
      <div className="flex items-center justify-between">
        <span className="text-xs text-text-secondary">⌘↵ to run</span>
        <PalmettoButton
          type="button"
          emphasis="primary"
          layout="hug"
          onClick={handleSubmit}
          disabled={isLoading || !description.trim()}
        >
          {isLoading ? 'Running…' : 'Run'}
        </PalmettoButton>
      </div>
    </div>
  );
};

const ImportBox = () => {
  const navigate = useNavigate();
  const [dumpText, setDumpText] = useState('');
  const [fileName, setFileName] = useState('');
  const [importRun, { isLoading }] = useImportAccountctlRunMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChooseFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setDumpText((ev.target?.result as string) ?? '');
    };
    reader.onerror = () => {
      toast.error('Failed to read file');
      setFileName('');
    };
    reader.readAsText(file);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!dumpText.trim()) return;
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(dumpText) as Record<string, unknown>;
    } catch {
      toast.error('Invalid JSON — the selected file does not appear to be a valid dump');
      return;
    }
    try {
      const result = await importRun({ dump: parsed }).unwrap();
      navigate(`/internal/accountctl/runs/${result.runId}`);
    } catch (err) {
      const apiMessage =
        err && typeof err === 'object' && 'data' in err
          ? (err as { data?: { message?: string } }).data?.message
          : undefined;
      toast.error(apiMessage ?? (err instanceof Error ? err.message : 'Import failed'));
    }
  }, [importRun, dumpText, navigate]);

  return (
    <div className="flex items-center gap-8">
      <PalmettoButton
        type="button"
        emphasis="secondary"
        layout="hug"
        onClick={handleChooseFile}
        disabled={isLoading}
      >
        Choose file
      </PalmettoButton>
      <span className="text-sm text-text-secondary">{fileName || 'No file chosen'}</span>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        className="sr-only"
        onChange={handleFileChange}
        disabled={isLoading}
      />
      <PalmettoButton
        type="button"
        emphasis="primary"
        layout="hug"
        onClick={handleSubmit}
        disabled={isLoading || !dumpText.trim()}
      >
        {isLoading ? 'Importing…' : 'Import'}
      </PalmettoButton>
    </div>
  );
};

interface ScenarioRowActionsProps {
  scenario: AccountctlScenarioSummary;
  onRun: (scenario: AccountctlScenarioSummary) => void;
}

const ScenarioRowActions = ({ scenario, onRun }: ScenarioRowActionsProps) => {
  const handleClick = useCallback(() => {
    onRun(scenario);
  }, [onRun, scenario]);
  return (
    <PalmettoButton type="button" emphasis="primary" size="small" onClick={handleClick}>
      Run
    </PalmettoButton>
  );
};

export const ScenariosPage = () => {
  const { data: scenarios = [], isLoading: isLoadingScenarios } = useListAccountctlScenariosQuery();
  const { data: runs = [], isLoading: isLoadingRuns } = useListAccountctlRunsQuery({ limit: RUN_HISTORY_LIMIT });

  const [selectedScenario, setSelectedScenario] = useState<AccountctlScenarioSummary | null>(null);
  const [scenariosExpanded, setScenariosExpanded] = useState(false);

  const handleOpenRun = useCallback((scenario: AccountctlScenarioSummary) => {
    setSelectedScenario(scenario);
  }, []);

  const handleCloseRun = useCallback(() => {
    setSelectedScenario(null);
  }, []);

  const handleToggleScenarios = useCallback(() => setScenariosExpanded((v) => !v), []);

  const scenarioColumns: Column[] = useMemo(
    () => [
      {
        heading: 'Scenario',
        dataKey: 'id',
        render: (_cell, row) => {
          const scenario = row as AccountctlScenarioSummary;
          return <span className="font-bold">{scenario.id}</span>;
        },
      },
      {
        heading: 'Description',
        dataKey: 'description',
      },
      {
        heading: '',
        dataKey: 'actions',
        width: 120,
        render: (_cell, row) => (
          <ScenarioRowActions scenario={row as AccountctlScenarioSummary} onRun={handleOpenRun} />
        ),
      },
    ],
    [handleOpenRun],
  );

  const runColumns: Column[] = useMemo(
    () => [
      {
        heading: 'Status',
        dataKey: 'status',
        width: 200,
        render: (_cell, row) => {
          const run = row as AccountctlRunSummary;
          return (
            <div className="flex flex-col gap-2">
              <StatusPill status={run.status} />
              {run.status === 'error' && run.error && (
                <span className="text-xs text-text-error" title={run.error}>
                  {run.error.length > 60 ? `${run.error.slice(0, 60)}…` : run.error}
                </span>
              )}
            </div>
          );
        },
      },
      {
        heading: 'Scenario',
        dataKey: 'scenarioId',
        render: (_cell, row) => {
          const run = row as AccountctlRunSummary;
          return (
            <Link to={`/internal/accountctl/runs/${run.id}`} className="text-text-link">
              {run.scenarioId}
            </Link>
          );
        },
      },
      {
        heading: 'User directions',
        dataKey: 'adHocDescription',
        render: (_cell, row) => {
          const run = row as AccountctlRunSummary;
          const desc = run.adHocDescription;
          if (!desc) return null;
          return (
            <span className="text-sm text-text-secondary">{desc.length > 60 ? `${desc.slice(0, 60)}…` : desc}</span>
          );
        },
      },
      {
        heading: 'Account',
        dataKey: 'accountId',
        width: 280,
        render: (_cell, row) => {
          const run = row as AccountctlRunSummary;
          if (!run.accountId) return null;
          return (
            <a href={`/accounts/${run.accountId}`} target="_blank" rel="noreferrer" className="text-text-link">
              {run.accountId}
            </a>
          );
        },
      },
      {
        heading: 'Started',
        dataKey: 'startedAt',
        width: 140,
        render: (_cell, row) => formatRelative((row as AccountctlRunSummary).startedAt),
      },
      {
        heading: '',
        dataKey: 'flagged',
        width: 32,
        render: (_cell, row) => {
          const run = row as AccountctlRunSummary;
          if (!run.flagged) return null;
          return (
            <span title={run.flagNote ?? 'Flagged for engineer review'} className="cursor-default">
              🚩
            </span>
          );
        },
      },
    ],
    [],
  );

  return (
    <DsmPageContainer title="Test Account Generator">
      <PageHeader title="Test Account Generator">
        <Link to="/internal/accountctl/test-users">
          <PalmettoButton type="button" emphasis="secondary">
            Manage test users
          </PalmettoButton>
        </Link>
      </PageHeader>

      <div className="mx-6 mb-24 flex flex-col gap-16">
        <ComposeBox />
        <div className="relative flex items-center gap-8">
          <div className="border-separator flex-1 border-t" />
          <span className="text-xs text-text-secondary">or import account json file</span>
          <div className="border-separator flex-1 border-t" />
        </div>
        <ImportBox />
        <div>
          <button
            type="button"
            className="flex items-center gap-8 text-left"
            onClick={handleToggleScenarios}
            aria-expanded={scenariosExpanded}
            aria-controls="scenario-list"
          >
            <h2 className="heading-200 text-text-primary">Scenarios</h2>
            <span className="text-sm text-text-secondary" aria-hidden="true">
              {scenariosExpanded ? '▲' : '▼'}
            </span>
          </button>
          {scenariosExpanded && (
            <div className="mt-16" id="scenario-list">
              <Table
                isLoading={isLoadingScenarios}
                rowKey="id"
                columns={scenarioColumns}
                rows={scenarios}
                isScrollable={{ x: true }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="mx-6 flex flex-col gap-16">
        <h2 className="heading-200 text-text-primary">Recent runs</h2>
        <Table isLoading={isLoadingRuns} rowKey="id" columns={runColumns} rows={runs} isScrollable={{ x: true }} />
      </div>

      {selectedScenario && <RunModal scenario={selectedScenario} onClose={handleCloseRun} />}
    </DsmPageContainer>
  );
};
